/**
 * Always-visible strip while Computers2Go support is signed in as a user
 * (BUILD-PLAN 2.6). Everything done is recorded under both names.
 *
 * "Return to my account" ends the support session (audited in the
 * practice's log and in Computers2Go's support access log). When it was
 * started from the C2G Admin console, the support account's own session
 * comes back and the console opens again; otherwise the browser is signed out.
 *
 * The strip sits above the shell, so it publishes its height as
 * `--vel-banner-h` and both shells (shell.css, classic.css) give it room:
 * they used to stay a full 100dvh tall and the strip pushed their bottom
 * edge (the sidebar's footer and collapse button) off the window
 * (Zac 2026-10-29). The text wraps and the button never leaves the screen.
 */
const BANNER_VAR = '--vel-banner-h';

/** Keeps `--vel-banner-h` on <html> equal to the strip's height while it is shown. */
function useBannerHeight(el: HTMLElement | null) {
  useLayoutEffect(() => {
    if (!el) return;
    const root = document.documentElement;
    const apply = () => root.style.setProperty(BANNER_VAR, `${el.offsetHeight}px`);
    apply();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(apply);
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      root.style.removeProperty(BANNER_VAR);
    };
  }, [el]);
}
import { SUPPORT_ADMIN_PATH } from '@velora/contracts/identity';
import { Button } from '@velora/ui';
import { Headset } from 'lucide-react';
import { useLayoutEffect, useState } from 'react';
import { identityApi } from '../api.js';
import { useSessionContext } from '../session.js';

function until(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export function ImpersonationBanner() {
  const { session, signOut } = useSessionContext();
  const [busy, setBusy] = useState(false);
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  useBannerHeight(session?.impersonator ? el : null);
  if (!session?.impersonator) return null;
  const back = async () => {
    setBusy(true);
    try {
      const r = await identityApi.endImpersonation();
      window.location.assign(r.restored ? SUPPORT_ADMIN_PATH : '/login');
    } catch {
      // The session may already be over (expired or revoked): sign out cleanly.
      await signOut();
    }
  };
  return (
    <div className="idn-imp" role="status" ref={setEl}>
      <Headset size={15} aria-hidden />
      <span className="idn-imp__text">
        <b>{session.impersonator.displayName}</b> (Computers2Go support) is signed in as{' '}
        <b>{session.user.displayName}</b> until {until(session.session.expiresAt)}. Everything done
        here is recorded under both names.
      </span>
      <Button size="sm" variant="secondary" disabled={busy} onClick={() => void back()}>
        Return to my account
      </Button>
    </div>
  );
}
